// ============================================
// Status Page - API Endpoint Health Monitoring
// ============================================

import { useState, useEffect, useCallback } from "react";
import { DropCatchService } from "../../services/dropcatchService";
import { GeminiService } from "../../services/geminiService";
import { ResendService } from "../../services/resendService";

interface StatusPageProps {
  dropcatchService: DropCatchService;
  geminiService: GeminiService;
  resendService: ResendService;
  className?: string;
}

interface EndpointStatus {
  name: string;
  endpoint: string;
  status: "checking" | "online" | "offline" | "degraded" | "idle";
  responseTime?: number;
  message?: string;
  lastChecked?: Date;
}

interface ServiceHealth {
  name: string;
  icon: string;
  endpoints: EndpointStatus[];
  overallStatus: "online" | "offline" | "degraded" | "checking";
}

/**
 * StatusPage Component
 *
 * Displays real-time health status of all API endpoints:
 * - DropCatch API endpoints
 * - Gemini AI API
 * - Resend Email API
 */
export function StatusPage({
  dropcatchService,
  geminiService,
  resendService,
  className = "",
}: StatusPageProps) {
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [lastFullCheck, setLastFullCheck] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Initialize services structure
  useEffect(() => {
    setServices([
      {
        name: "DROPCATCH API",
        icon: "🌐",
        overallStatus: "checking",
        endpoints: [
          { name: "Authentication", endpoint: "/Authorize", status: "idle" },
          { name: "Auctions Search", endpoint: "/v2/auctions", status: "idle" },
          { name: "My Bids", endpoint: "/v2/bids", status: "idle" },
          { name: "Backorders", endpoint: "/v2/backorders", status: "idle" },
          { name: "Auction History", endpoint: "/v2/history/auctions", status: "idle" },
          { name: "Backorder History", endpoint: "/v2/history/backorders", status: "idle" },
        ],
      },
      {
        name: "GEMINI AI",
        icon: "🤖",
        overallStatus: "checking",
        endpoints: [
          { name: "Domain Analysis", endpoint: "gemini-1.5-flash", status: "idle" },
          { name: "Chat Completion", endpoint: "gemini-1.5-flash", status: "idle" },
        ],
      },
      {
        name: "RESEND EMAIL",
        icon: "📧",
        overallStatus: "checking",
        endpoints: [
          { name: "Email Delivery", endpoint: "/emails", status: "idle" },
        ],
      },
    ]);
  }, []);

  // Update single endpoint status
  const updateEndpoint = useCallback((
    serviceName: string,
    endpointName: string,
    update: Partial<EndpointStatus>
  ) => {
    setServices((prev) =>
      prev.map((service) => {
        if (service.name !== serviceName) return service;

        const endpoints = service.endpoints.map((ep) =>
          ep.name === endpointName ? { ...ep, ...update } : ep
        );

        // Calculate overall status
        const statuses = endpoints.map((e) => e.status);
        let overallStatus: ServiceHealth["overallStatus"] = "online";
        if (statuses.some((s) => s === "checking")) {
          overallStatus = "checking";
        } else if (statuses.every((s) => s === "offline")) {
          overallStatus = "offline";
        } else if (statuses.some((s) => s === "offline" || s === "degraded")) {
          overallStatus = "degraded";
        }

        return { ...service, endpoints, overallStatus };
      })
    );
  }, []);

  // Check DropCatch endpoints
  const checkDropCatch = useCallback(async () => {
    const serviceName = "DROPCATCH API";

    // Auth check
    updateEndpoint(serviceName, "Authentication", { status: "checking" });
    const authStart = Date.now();
    try {
      const result = await dropcatchService.testConnection();
      updateEndpoint(serviceName, "Authentication", {
        status: result.success ? "online" : "offline",
        responseTime: Date.now() - authStart,
        message: result.message,
        lastChecked: new Date(),
      });

      if (!result.success) {
        // If auth fails, mark all others as offline
        ["Auctions Search", "My Bids", "Backorders", "Auction History", "Backorder History"].forEach((name) => {
          updateEndpoint(serviceName, name, {
            status: "offline",
            message: "Authentication required",
            lastChecked: new Date(),
          });
        });
        return;
      }
    } catch (error) {
      updateEndpoint(serviceName, "Authentication", {
        status: "offline",
        responseTime: Date.now() - authStart,
        message: error instanceof Error ? error.message : "Connection failed",
        lastChecked: new Date(),
      });
      return;
    }

    // Auctions check
    updateEndpoint(serviceName, "Auctions Search", { status: "checking" });
    const auctionsStart = Date.now();
    try {
      await dropcatchService.searchAuctions({ showAllActive: true, size: 1 });
      updateEndpoint(serviceName, "Auctions Search", {
        status: "online",
        responseTime: Date.now() - auctionsStart,
        message: "Endpoint responding",
        lastChecked: new Date(),
      });
    } catch (error) {
      const is404 = error instanceof Error && error.message.includes("404");
      updateEndpoint(serviceName, "Auctions Search", {
        status: is404 ? "online" : "offline",
        responseTime: Date.now() - auctionsStart,
        message: is404 ? "No auctions (normal)" : (error instanceof Error ? error.message : "Failed"),
        lastChecked: new Date(),
      });
    }

    // Bids check
    updateEndpoint(serviceName, "My Bids", { status: "checking" });
    const bidsStart = Date.now();
    try {
      await dropcatchService.getBids({ size: 1 });
      updateEndpoint(serviceName, "My Bids", {
        status: "online",
        responseTime: Date.now() - bidsStart,
        message: "Endpoint responding",
        lastChecked: new Date(),
      });
    } catch (error) {
      const is404 = error instanceof Error && error.message.includes("404");
      updateEndpoint(serviceName, "My Bids", {
        status: is404 ? "online" : "offline",
        responseTime: Date.now() - bidsStart,
        message: is404 ? "No bids (normal)" : (error instanceof Error ? error.message : "Failed"),
        lastChecked: new Date(),
      });
    }

    // Backorders check
    updateEndpoint(serviceName, "Backorders", { status: "checking" });
    const backordersStart = Date.now();
    try {
      await dropcatchService.getBackorders({ size: 1 });
      updateEndpoint(serviceName, "Backorders", {
        status: "online",
        responseTime: Date.now() - backordersStart,
        message: "Endpoint responding",
        lastChecked: new Date(),
      });
    } catch (error) {
      const is404 = error instanceof Error && error.message.includes("404");
      updateEndpoint(serviceName, "Backorders", {
        status: is404 ? "online" : "offline",
        responseTime: Date.now() - backordersStart,
        message: is404 ? "No backorders (normal)" : (error instanceof Error ? error.message : "Failed"),
        lastChecked: new Date(),
      });
    }

    // Auction History check
    updateEndpoint(serviceName, "Auction History", { status: "checking" });
    const auctionHistStart = Date.now();
    try {
      await dropcatchService.getAuctionHistory({ size: 1 });
      updateEndpoint(serviceName, "Auction History", {
        status: "online",
        responseTime: Date.now() - auctionHistStart,
        message: "Endpoint responding",
        lastChecked: new Date(),
      });
    } catch (error) {
      const is404 = error instanceof Error && error.message.includes("404");
      updateEndpoint(serviceName, "Auction History", {
        status: is404 ? "online" : "offline",
        responseTime: Date.now() - auctionHistStart,
        message: is404 ? "No history (normal)" : (error instanceof Error ? error.message : "Failed"),
        lastChecked: new Date(),
      });
    }

    // Backorder History check
    updateEndpoint(serviceName, "Backorder History", { status: "checking" });
    const backorderHistStart = Date.now();
    try {
      await dropcatchService.getBackorderHistory({ size: 1 });
      updateEndpoint(serviceName, "Backorder History", {
        status: "online",
        responseTime: Date.now() - backorderHistStart,
        message: "Endpoint responding",
        lastChecked: new Date(),
      });
    } catch (error) {
      const is404 = error instanceof Error && error.message.includes("404");
      updateEndpoint(serviceName, "Backorder History", {
        status: is404 ? "online" : "offline",
        responseTime: Date.now() - backorderHistStart,
        message: is404 ? "No history (normal)" : (error instanceof Error ? error.message : "Failed"),
        lastChecked: new Date(),
      });
    }
  }, [dropcatchService, updateEndpoint]);

  // Check Gemini endpoints
  const checkGemini = useCallback(async () => {
    const serviceName = "GEMINI AI";

    // Domain Analysis check
    updateEndpoint(serviceName, "Domain Analysis", { status: "checking" });
    const analysisStart = Date.now();
    try {
      await geminiService.quickCheck("test.com");
      updateEndpoint(serviceName, "Domain Analysis", {
        status: "online",
        responseTime: Date.now() - analysisStart,
        message: "AI responding",
        lastChecked: new Date(),
      });
    } catch (error) {
      updateEndpoint(serviceName, "Domain Analysis", {
        status: "offline",
        responseTime: Date.now() - analysisStart,
        message: error instanceof Error ? error.message : "API key may be invalid",
        lastChecked: new Date(),
      });
    }

    // Chat check (uses same API, just mark as online if analysis works)
    updateEndpoint(serviceName, "Chat Completion", { status: "checking" });
    try {
      // Quick test
      await geminiService.quickCheck("example.com");
      updateEndpoint(serviceName, "Chat Completion", {
        status: "online",
        responseTime: Date.now() - analysisStart,
        message: "Chat available",
        lastChecked: new Date(),
      });
    } catch (error) {
      updateEndpoint(serviceName, "Chat Completion", {
        status: "offline",
        message: "Chat unavailable",
        lastChecked: new Date(),
      });
    }
  }, [geminiService, updateEndpoint]);

  // Check Resend endpoints
  const checkResend = useCallback(async () => {
    const serviceName = "RESEND EMAIL";

    updateEndpoint(serviceName, "Email Delivery", { status: "checking" });
    const start = Date.now();

    // Just check if API key is configured
    const isConfigured = resendService.isConfigured();
    updateEndpoint(serviceName, "Email Delivery", {
      status: isConfigured ? "online" : "degraded",
      responseTime: Date.now() - start,
      message: isConfigured ? "API key configured" : "No API key configured",
      lastChecked: new Date(),
    });
  }, [resendService, updateEndpoint]);

  // Run all checks
  const runAllChecks = useCallback(async () => {
    setIsChecking(true);

    await Promise.all([
      checkDropCatch(),
      checkGemini(),
      checkResend(),
    ]);

    setLastFullCheck(new Date());
    setIsChecking(false);
  }, [checkDropCatch, checkGemini, checkResend]);

  // Initial check on mount
  useEffect(() => {
    runAllChecks();
  }, []);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(runAllChecks, 60000); // Every 60 seconds
    return () => clearInterval(interval);
  }, [autoRefresh, runAllChecks]);

  // Get status color
  const getStatusColor = (status: EndpointStatus["status"]) => {
    switch (status) {
      case "online": return "text-green-400 border-green-500";
      case "offline": return "text-red-400 border-red-500";
      case "degraded": return "text-yellow-400 border-yellow-500";
      case "checking": return "text-blue-400 border-blue-500 animate-pulse";
      default: return "text-green-700 border-green-800";
    }
  };

  const getStatusBg = (status: EndpointStatus["status"]) => {
    switch (status) {
      case "online": return "bg-green-500";
      case "offline": return "bg-red-500";
      case "degraded": return "bg-yellow-500";
      case "checking": return "bg-blue-500 animate-pulse";
      default: return "bg-green-800";
    }
  };

  // Calculate overall system status
  const overallStatus = services.every((s) => s.overallStatus === "online")
    ? "ALL SYSTEMS OPERATIONAL"
    : services.every((s) => s.overallStatus === "offline")
    ? "ALL SYSTEMS DOWN"
    : services.some((s) => s.overallStatus === "checking")
    ? "CHECKING STATUS..."
    : "PARTIAL OUTAGE";

  const overallColor = overallStatus === "ALL SYSTEMS OPERATIONAL"
    ? "text-green-400"
    : overallStatus === "ALL SYSTEMS DOWN"
    ? "text-red-400"
    : overallStatus === "CHECKING STATUS..."
    ? "text-blue-400"
    : "text-yellow-400";

  return (
    <div className={`flex flex-col h-full bg-black ${className}`}>
      {/* Header */}
      <div className="px-6 py-3 border-b-2 border-green-500 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 animate-pulse" />
            <span className="text-green-400 font-bold tracking-wider">
              SYSTEM STATUS
            </span>
          </div>
          <span className={`text-sm font-bold ${overallColor}`}>
            {overallStatus}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-green-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-green-500"
            />
            AUTO-REFRESH
          </label>
          <button
            onClick={runAllChecks}
            disabled={isChecking}
            className="px-3 py-1 border border-green-500 text-green-500 text-xs hover:bg-green-500 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isChecking ? "[CHECKING...]" : "[CHECK NOW]"}
          </button>
        </div>
      </div>

      {/* Last check info */}
      {lastFullCheck && (
        <div className="px-6 py-2 border-b border-green-900 text-xs text-green-600">
          Last full check: {lastFullCheck.toLocaleTimeString()}
        </div>
      )}

      {/* Services Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div
              key={service.name}
              className="border-2 border-green-700 bg-black"
            >
              {/* Service Header */}
              <div className="px-4 py-3 border-b border-green-700 flex items-center justify-between bg-green-900/20">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{service.icon}</span>
                  <span className="text-green-400 font-bold tracking-wider text-sm">
                    {service.name}
                  </span>
                </div>
                <div className={`w-3 h-3 rounded-full ${getStatusBg(service.overallStatus)}`} />
              </div>

              {/* Endpoints List */}
              <div className="p-4 space-y-3">
                {service.endpoints.map((endpoint) => (
                  <div
                    key={endpoint.name}
                    className={`border p-3 ${getStatusColor(endpoint.status)}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{endpoint.name}</span>
                      <span className="text-xs uppercase tracking-wider">
                        {endpoint.status}
                      </span>
                    </div>
                    <div className="text-xs text-green-700 font-mono">
                      {endpoint.endpoint}
                    </div>
                    {endpoint.responseTime !== undefined && (
                      <div className="text-xs text-green-600 mt-1">
                        Response: {endpoint.responseTime}ms
                      </div>
                    )}
                    {endpoint.message && (
                      <div className="text-xs text-green-700 mt-1 truncate">
                        {endpoint.message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Endpoint Parameter Reference */}
        <div className="mt-8 border-2 border-green-700">
          <div className="px-4 py-3 border-b border-green-700 bg-green-900/20">
            <span className="text-green-400 font-bold tracking-wider text-sm">
              API PARAMETER REFERENCE
            </span>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
            {/* Auctions */}
            <div className="border border-green-800 p-3">
              <div className="text-green-400 font-bold mb-2">AUCTIONS</div>
              <div className="text-green-600 space-y-1 font-mono">
                <div>showAllActive: boolean</div>
                <div>searchTerm: string</div>
                <div>size: number</div>
                <div>tlds: string[]</div>
                <div>types: Dropped|PrivateSeller|Prerelease</div>
                <div>hasBids: boolean</div>
                <div>highBidMin/Max: number</div>
                <div>sort: EndTime|HighBid|Domain|Bids</div>
              </div>
            </div>

            {/* Bids */}
            <div className="border border-green-800 p-3">
              <div className="text-green-400 font-bold mb-2">BIDS</div>
              <div className="text-green-600 space-y-1 font-mono">
                <div>size: number</div>
                <div>winning: boolean</div>
                <div>sort: HighBid|MaxBid|Domain</div>
              </div>
            </div>

            {/* Backorders */}
            <div className="border border-green-800 p-3">
              <div className="text-green-400 font-bold mb-2">BACKORDERS</div>
              <div className="text-green-600 space-y-1 font-mono">
                <div>size: number</div>
                <div>tld: string</div>
                <div>type: Standard</div>
                <div>sort: Domain|DropDate|Type|MaxBid</div>
              </div>
            </div>

            {/* History */}
            <div className="border border-green-800 p-3">
              <div className="text-green-400 font-bold mb-2">HISTORY</div>
              <div className="text-green-600 space-y-1 font-mono">
                <div>size: number</div>
                <div>searchTerm: string</div>
                <div>sort: Updated|Amount|Domain</div>
              </div>
            </div>

            {/* Gemini */}
            <div className="border border-green-800 p-3">
              <div className="text-green-400 font-bold mb-2">GEMINI AI</div>
              <div className="text-green-600 space-y-1 font-mono">
                <div>analyzeDomain(domain, price)</div>
                <div>quickCheck(domain)</div>
                <div>suggestDomains(keywords)</div>
                <div>chatMessage(history, msg)</div>
              </div>
            </div>

            {/* Resend */}
            <div className="border border-green-800 p-3">
              <div className="text-green-400 font-bold mb-2">RESEND EMAIL</div>
              <div className="text-green-600 space-y-1 font-mono">
                <div>sendEmail(to, subject, html)</div>
                <div>sendAlertEmail(alert)</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatusPage;
